// Gamepads (Xbox, PlayStation, generic) through the browser's Gamepad API, polled once per frame.
// Fixed "standard" layout, shown in the settings: the stick and the D-pad steer, A fires, bumpers shift.
import type { GameAction } from './input.js';

export interface PadButtonLike { pressed: boolean; value: number }
export interface PadLike { index: number; id: string; mapping: string; connected: boolean; buttons: readonly PadButtonLike[]; axes: readonly number[] }

/** Button index (W3C standard mapping) → action. */
export const PAD_BUTTONS: Readonly<Record<number, GameAction>> = {
  0: 'fire', // A / Cross
  7: 'fire', // right trigger
  1: 'look', // B / Circle
  3: 'camera', // Y / Triangle
  5: 'gearUp', // right bumper
  12: 'gearUp', // D-pad up
  4: 'gearDown', // left bumper
  13: 'gearDown', // D-pad down
  14: 'steerLeft', // D-pad left
  15: 'steerRight', // D-pad right
  9: 'pause', // Start / Options
};

/** Stick hysteresis: pushed past PRESS, released under RELEASE, so a stick resting near the threshold does not chatter. */
export const STICK_PRESS = 0.55, STICK_RELEASE = 0.35;
/** Analog triggers count as pressed past this value. */
export const TRIGGER_PRESS = 0.5;

/** Controls held on a pad (control id → action), from its state and the controls held on the previous poll. */
export function padControls(pad: PadLike, prev: ReadonlyMap<string, GameAction>): Map<string, GameAction> {
  const out = new Map<string, GameAction>();
  for (const [i, action] of Object.entries(PAD_BUTTONS)) {
    const b = pad.buttons[+i];
    if (b && (b.pressed || b.value > TRIGGER_PRESS)) out.set('b' + i, action);
  }
  const x = Number.isFinite(pad.axes[0]) ? pad.axes[0] : 0;
  const held = (id: string, v: number) => v > (prev.has(id) ? STICK_RELEASE : STICK_PRESS);
  if (held('sl', -x)) out.set('sl', 'steerLeft');
  if (held('sr', x)) out.set('sr', 'steerRight');
  return out;
}

/** Differences between two polls, as presses and releases. */
export function padChanges(prev: ReadonlyMap<string, GameAction>, next: ReadonlyMap<string, GameAction>) {
  const changes: { control: string; action: GameAction; down: boolean }[] = [];
  for (const [c, a] of prev) if (next.get(c) !== a) changes.push({ control: c, action: a, down: false });
  for (const [c, a] of next) if (prev.get(c) !== a) changes.push({ control: c, action: a, down: true });
  return changes;
}

/** Short readable name: browsers append vendor and product ids ("Xbox Wireless Controller (STANDARD GAMEPAD Vendor: 045e…)"). */
export function padName(id: string): string {
  const n = id.replace(/\s*\((?:STANDARD GAMEPAD|Vendor:)[^)]*\)\s*/gi, ' ').replace(/^[0-9a-f]{4}-[0-9a-f]{4}-/i, '').replace(/\s+/g, ' ').trim();
  return (n || id).slice(0, 60);
}

type Press = (control: string, action: GameAction, down: boolean) => void;

/** Polls the connected pads while a session runs, and reports which pads are present. */
export class GamepadPoller extends EventTarget {
  private held = new Map<number, Map<string, GameAction>>();
  private raf = 0;
  private press: Press | null = null;

  constructor(private nav: { getGamepads?: () => (PadLike | null)[] } | null = typeof navigator === 'undefined' ? null : (navigator as unknown as { getGamepads?: () => (PadLike | null)[] })) {
    super();
    if (typeof window === 'undefined') return;
    // browsers only reveal a pad once one of its buttons has been pressed (fingerprinting protection)
    window.addEventListener('gamepadconnected', () => this.dispatchEvent(new Event('change')));
    window.addEventListener('gamepaddisconnected', e => { this.drop((e as GamepadEvent).gamepad.index); this.dispatchEvent(new Event('change')); });
  }

  /** Connected pads (the Gamepad API can throw when a permissions policy forbids it). */
  pads(): PadLike[] {
    try { return (this.nav?.getGamepads?.() ?? []).filter((p): p is PadLike => !!p && p.connected); } catch { return []; }
  }
  get connected() { return this.pads().length > 0; }

  start(press: Press) {
    this.press = press;
    if (!this.raf && typeof requestAnimationFrame !== 'undefined') this.raf = requestAnimationFrame(() => this.loop());
  }
  stop() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    for (const i of [...this.held.keys()]) this.drop(i);
    this.press = null;
  }

  /** One poll; public so it can be driven without a frame loop. */
  poll() {
    const seen = new Set<number>();
    for (const pad of this.pads()) {
      seen.add(pad.index);
      const prev = this.held.get(pad.index) ?? new Map<string, GameAction>(), next = padControls(pad, prev);
      for (const c of padChanges(prev, next)) this.press?.(`${pad.index}.${c.control}`, c.action, c.down);
      this.held.set(pad.index, next);
    }
    for (const i of [...this.held.keys()]) if (!seen.has(i)) this.drop(i);
  }

  private loop() {
    this.poll();
    this.raf = requestAnimationFrame(() => this.loop());
  }
  private drop(index: number) {
    const prev = this.held.get(index);
    if (!prev) return;
    for (const [c, a] of prev) this.press?.(`${index}.${c}`, a, false);
    this.held.delete(index);
  }
}
